import React from 'react';
import { Layers, AlertTriangle, Ban, Target, CircleSlash, GitBranch } from 'lucide-react';
import type { WorkstreamState, Metrics } from './pmApi';

/**
 * Department navigation.
 *
 * The master plan ships as five per-department exports (S1 IT & M365, S2
 * Podiatry Launch, S3 Responsible AI, S4 HR & Recruiting, S5 Finance), and
 * that is how the business is actually run — so department is the primary
 * axis of this workspace rather than a filter buried in a dropdown.
 *
 * Each row carries its own health and counts, so the rail doubles as an
 * at-a-glance status board: you can see which department is on fire without
 * selecting anything.
 */

export type QuickFilter = 'none' | 'overdue' | 'blocked' | 'milestones' | 'noNextAction' | 'criticalPath';

/** Long plan names are unusable in a 240px rail; these keep the meaning. */
const SHORT_NAME: Record<string, string> = {
  'IT & Microsoft 365': 'IT & M365',
  'Podiatry Business Launch': 'Podiatry Launch',
  'Responsible AI Program': 'Responsible AI',
  'HR & Recruiting': 'HR & Recruiting',
  'Finance & Controller Operations': 'Finance',
};

const HEALTH_DOT: Record<string, string> = {
  'ON TRACK': 'bg-emerald-500',
  WATCH: 'bg-amber-500',
  'AT RISK': 'bg-orange-500',
  BLOCKED: 'bg-red-500',
};

const QUICK_FILTERS: Array<{ key: QuickFilter; label: string; icon: React.ReactNode; count: (m: Metrics) => number }> = [
  { key: 'overdue', label: 'Overdue', icon: <AlertTriangle className="h-3.5 w-3.5" />, count: (m) => m.overdue },
  { key: 'blocked', label: 'Blocked', icon: <Ban className="h-3.5 w-3.5" />, count: (m) => m.blocked },
  { key: 'milestones', label: 'Milestones', icon: <Target className="h-3.5 w-3.5" />, count: (m) => m.milestonesBreached },
  { key: 'noNextAction', label: 'No next action', icon: <CircleSlash className="h-3.5 w-3.5" />, count: (m) => m.missingNextAction },
  { key: 'criticalPath', label: 'Critical path', icon: <GitBranch className="h-3.5 w-3.5" />, count: () => 0 },
];

export default function DepartmentRail({
  workstreams, metrics, selected, onSelect, quick, onQuick,
}: {
  workstreams: WorkstreamState[];
  metrics: Metrics | undefined;
  selected: string | 'all';
  onSelect: (id: string | 'all') => void;
  quick: QuickFilter;
  onQuick: (q: QuickFilter) => void;
}) {
  const totalOpen = workstreams.reduce((n, w) => n + w.stats.open, 0);
  const totalOverdue = workstreams.reduce((n, w) => n + w.stats.overdue, 0);

  const rowClass = (active: boolean) =>
    `flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${
      active ? 'bg-primary/10 font-medium text-primary' : 'text-foreground/80 hover:bg-muted'
    }`;

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Departments
        </p>
        <div className="space-y-0.5">
          <button type="button" className={rowClass(selected === 'all')} onClick={() => onSelect('all')}>
            <Layers className="h-4 w-4 shrink-0 opacity-70" />
            <span className="flex-1 truncate">All departments</span>
            <span className="shrink-0 tabular-nums text-xs text-muted-foreground">{totalOpen}</span>
          </button>

          {workstreams.map((w) => {
            const active = selected === w.id;
            return (
              <button key={w.id} type="button" className={rowClass(active)} onClick={() => onSelect(w.id)}>
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${HEALTH_DOT[w.health] || 'bg-muted-foreground'}`}
                  title={w.health}
                  aria-label={w.health}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{SHORT_NAME[w.name] || w.name}</span>
                  <span className="block text-[11px] font-normal text-muted-foreground">
                    {w.stats.open} open
                    {w.stats.overdue ? <span className="text-orange-600 dark:text-orange-400"> · {w.stats.overdue} late</span> : null}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {totalOverdue ? (
          <p className="mt-2 px-2.5 text-[11px] text-muted-foreground">
            <span className="tabular-nums font-medium text-orange-600 dark:text-orange-400">{totalOverdue}</span> items past due across all departments
          </p>
        ) : null}
      </div>

      <div>
        <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Filter
        </p>
        <div className="space-y-0.5">
          {QUICK_FILTERS.map((f) => {
            const active = quick === f.key;
            const count = metrics ? f.count(metrics) : 0;
            return (
              <button
                key={f.key}
                type="button"
                className={rowClass(active)}
                onClick={() => onQuick(active ? 'none' : f.key)}
                aria-pressed={active}
              >
                <span className="shrink-0 opacity-70">{f.icon}</span>
                <span className="flex-1 truncate">{f.label}</span>
                {count ? <span className="shrink-0 tabular-nums text-xs text-muted-foreground">{count}</span> : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
